import { cn } from '@/app/lib/utils';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import { useParams } from 'next/navigation';
import CustomSelect, { Option } from '../ui/CustomSelect';
import React, { useState } from 'react';
import { getPersonas } from '@/api/personas';
import { useQuery } from '@tanstack/react-query';
import ToggleInlineEdit from '../ui/ToggleInlineEdit';
import OpenButton from '../ui/OpenButton';
import { useContactDetailsDrawer } from '@/app/context/contact-details-drawer';
import QualificationPill from './QualificationPill';

type Props = {
  contact: {
    id: string;
    name: string;
    linkedin?: string;
    title?: string;
    profile_pic: string;
    persona?: {
      id?: string | undefined;
      short_name?: string | undefined;
      reason?: string;
      qualification_reason?: string;
      editable?: boolean;
      onEdit?: ({ id, name }: { id: string; name: string }) => void;
    };
  };
  clickable?: boolean;
  wrapperClassName?: string;
  showArrow?: boolean;
  avatarSize?: number;
  avatarClassName?: string;
  contactNameClass?: string;
  contactTitleClass?: string;
  inputWrapperClassName?: string;
  isEditable?: boolean;
  alwaysShowArrow?: boolean;
  handleSave?: (value: string) => void;
  contactStatusSelect?: React.ReactNode;
  onOpen?: () => void;
  titleSuffix?: React.ReactNode;
  footer?: React.ReactNode;
  qualificationTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  onLinkedinClick?: () => void;
};

const ContactMiniCard = React.memo(
  ({
    contact,
    clickable = true,
    wrapperClassName,
    showArrow = false,
    avatarSize = 36,
    avatarClassName,
    contactNameClass,
    contactTitleClass,
    contactStatusSelect,
    inputWrapperClassName,
    isEditable = false,
    alwaysShowArrow = false,
    handleSave = () => {},
    onOpen,
    titleSuffix,
    footer,
    qualificationTooltipSide,
    onLinkedinClick,
  }: Props) => {
    const [selectedPersona, setSelectedPersona] = useState<Option | null>({
      id: contact?.persona?.id || '',
      value: contact?.persona?.short_name || '',
    });
    const { clientId } = useParams<{ clientId: string }>();
    const contactDetailsDrawer = useContactDetailsDrawer();

    const { data: personaData } = useQuery({
      queryKey: ['personas', clientId],
      queryFn: () => getPersonas({ clientId: clientId || '', limit: 100 }),
      enabled: !!contact?.persona?.editable,
      staleTime: Infinity,
    });

    const handleUpdatePersona = (option: Option) => {
      setSelectedPersona(option);
      contact?.persona?.onEdit?.({ id: option.id as string, name: option.value as string });
    };

    return (
      <a
        className={cn(
          'flex items-center gap-2 overflow-hidden',
          clickable ? 'cursor-pointer select-none' : 'cursor-default',
          wrapperClassName
        )}
        onClick={e => {
          if (!clickable) return;
          e.preventDefault();
          e.stopPropagation();
          contactDetailsDrawer?.openContactDetails(contact.id);
        }}
      >
        <Avatar
          name={contact?.name || '-'}
          src={contact?.profile_pic}
          isExternalUrl
          size={avatarSize}
          initialsClassName="rounded-full text-sm"
          className={avatarClassName}
          onClick={() => {
            if (isEditable) {
              contactDetailsDrawer?.openContactDetails(contact.id);
            }
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex items-center gap-2 overflow-hidden">
            <div
              className={cn(
                'flex items-center gap-2 overflow-hidden',
                isEditable ? 'gap-1' : 'gap-2 min-h-[22px] pr-1'
              )}
            >
              {isEditable ? (
                <div
                  className={cn(
                    'flex flex-1 overflow-hidden pointer-events-auto h-[46px]',
                    inputWrapperClassName
                  )}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <ToggleInlineEdit
                    value={contact?.name}
                    wrapperClassName="w-fit max-w-full overflow-hidden"
                    textWrapperClassName="overflow-hidden h-[46px] items-center flex"
                    inputClassName="px-1"
                    view={
                      <div
                        className={cn(
                          'max-w-full items-center text-foreground text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap',
                          contactNameClass
                        )}
                      >
                        {contact?.name || '-'}
                      </div>
                    }
                    onSave={value => {
                      handleSave?.(value.toString());
                    }}
                  />
                </div>
              ) : (
                <span
                  className={cn(
                    'text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap',
                    contactNameClass
                  )}
                >
                  {contact?.name || '-'}
                </span>
              )}
              {contact?.linkedin && (
                <div
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onLinkedinClick?.();
                    const url = contact?.linkedin?.startsWith('http')
                      ? contact?.linkedin
                      : `https://${contact?.linkedin}`;
                    window.open(url, '_blank');
                  }}
                  className="hover:underline text-[#0077B5] inline-flex items-center cursor-pointer pointer-events-auto"
                >
                  <Icon name="linkedin-filled" size={4} />
                </div>
              )}
              {contact?.persona ? (
                contact?.persona?.editable ? (
                  <div className="flex items-center gap-1 pointer-events-auto">
                    <CustomSelect
                      defaultValue={selectedPersona?.id}
                      options={
                        personaData?.data?.map(persona => ({
                          id: persona.id,
                          value: persona.short_name,
                          icon: 'user-follow-line',
                        })) || []
                      }
                      itemClassNames="border border-border rounded gap-1 mb-2 last:mb-0"
                      showScrollButtons={true}
                      contentClassNames="px-1.5 py-1.5 min-w-[230px]"
                      triggerPill
                      triggerPillProps={{
                        color: selectedPersona?.id ? 'info' : 'neutral',
                        variant: selectedPersona?.id ? 'soft' : 'outline',
                        className: 'text-xs uppercase h-5 cursor-pointer',
                        icon: <Icon name="user-follow-line" size={3} />,
                      }}
                      onOptionSelect={value => {
                        const selectedValue = value as Option;
                        handleUpdatePersona(selectedValue);
                      }}
                    />
                  </div>
                ) : (
                  contact?.persona?.short_name &&
                  contact?.persona?.id && (
                    <QualificationPill
                      type="persona"
                      label={contact.persona.short_name}
                      reason={contact.persona.reason ?? contact.persona.qualification_reason}
                      tooltipSide={qualificationTooltipSide}
                    />
                  )
                )
              ) : null}
              {contactStatusSelect}
              {showArrow && contact?.id && (onOpen || contactDetailsDrawer) && (
                <OpenButton
                  url=""
                  alwaysShow={alwaysShowArrow}
                  onOpen={
                    onOpen ||
                    (contactDetailsDrawer
                      ? () => contactDetailsDrawer.openContactDetails(contact.id)
                      : undefined)
                  }
                />
              )}
            </div>
          </div>
          {(contact?.title || titleSuffix) && (
            <div className="flex min-w-0 items-center gap-2 overflow-hidden">
              {contact?.title && (
                <span
                  className={cn(
                    'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground',
                    contactTitleClass
                  )}
                >
                  {contact?.title}
                </span>
              )}
              {titleSuffix}
            </div>
          )}
          {footer}
        </div>
      </a>
    );
  }
);

ContactMiniCard.displayName = 'ContactMiniCard';

export default ContactMiniCard;
